import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import AdminCheckInPanel from './AdminCheckInPanel';
import { formatClubDate, formatClubTime } from '../../utils/clubTime';
import { useAuth } from '../../contexts/AuthContext';
import { logAuditEntry } from '../../utils/auditLog';
import RemoveCheckInDialog from './RemoveCheckInDialog';
import RowMenu from './RowMenu';
import UndoCheckInDialog from './UndoCheckInDialog';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { recordManualPayment, reverseManualPayment } from '../../utils/recordManualPayment';

/** Paid = money has moved (same rule as the check-in duplicate guard, #84). */
export function isRegistrationPaid(reg: { paymentStatus?: string | null; squarePaymentId?: string | null }): boolean {
  return reg.paymentStatus === 'RECEIVED' || !!reg.squarePaymentId;
}

const client = generateClient({ authMode: 'userPool' });

interface CompetitionDayDashboardProps {
  eventId: string;
}

type PaymentStatusFilter = 'ALL' | 'PENDING' | 'RECEIVED';
type RegistrationStatusFilter = 'ALL' | 'PENDING' | 'FINALIZED';

interface MemberRenewalInfo {
  renewalStatus: string | null;
  /** #163 follow-up: the member's household, for "Mark family paid". */
  familyGroupId?: string | null;
}

/**
 * Column sorting for the check-in table. Mirrors the established pattern in
 * MemberManagement.tsx (SortableTh + a sort-value function + missing-last
 * comparator) rather than introducing a second convention.
 */
type RegSortKey =
  | 'participant'
  | 'contact'
  | 'type'
  | 'membership'
  | 'checkInTime'
  | 'fee'
  | 'payment'
  | 'status';

/** Clickable column header. Keyboard-operable and announces sort state to screen readers. */
const SortableTh: React.FC<{
  label: string;
  columnKey: RegSortKey;
  activeKey: RegSortKey;
  dir: 'asc' | 'desc';
  onSort: (key: RegSortKey) => void;
  sortHint?: string;
  className?: string;
}> = ({ label, columnKey, activeKey, dir, onSort, sortHint, className }) => {
  const isActive = columnKey === activeKey;
  return (
    <th
      scope="col"
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        title={`Sort by ${sortHint ?? label}`}
        className={`group inline-flex items-center gap-1 uppercase tracking-wider rounded focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
          isActive ? 'text-fishing-blue' : 'hover:text-gray-700'
        }`}
      >
        {label}
        <span
          aria-hidden="true"
          className={isActive ? '' : 'text-gray-300 group-hover:text-gray-400'}
        >
          {isActive ? (dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
};

/**
 * Ranks payment/registration state deliberately rather than alphabetically, so
 * sorting surfaces what an organiser on the day actually wants first: who still
 * owes money, and who is not yet finalised.
 */
const PAYMENT_ORDER: Record<string, number> = { PENDING: 0, RECEIVED: 1 };
const STATUS_ORDER: Record<string, number> = { PENDING: 0, FINALIZED: 1 };

function regSortValue(
  reg: any,
  key: RegSortKey,
  renewalStatus: string | null,
): string | number | null {
  switch (key) {
    case 'participant':
      return reg.participantName || null;
    case 'contact':
      return reg.participantEmail || reg.participantPhone || null;
    case 'type':
      return reg.registrationType || null;
    case 'membership':
      // Non-members sort last; otherwise by renewal status text.
      return reg.memberId ? renewalStatus || 'ACTIVE' : null;
    case 'checkInTime':
      return reg.checkInTimestamp ? new Date(reg.checkInTimestamp).getTime() : null;
    case 'fee':
      return typeof reg.feeAmount === 'number' ? reg.feeAmount : null;
    case 'payment':
      return PAYMENT_ORDER[reg.paymentStatus] ?? null;
    case 'status':
      return STATUS_ORDER[reg.registrationStatus] ?? null;
    default:
      return null;
  }
}

function compareRegs(
  a: any,
  b: any,
  key: RegSortKey,
  dir: 'asc' | 'desc',
  renewalOf: (reg: any) => string | null,
): number {
  const av = regSortValue(a, key, renewalOf(a));
  const bv = regSortValue(b, key, renewalOf(b));

  // Missing values always sort last regardless of direction, so blank rows never
  // push to the top.
  if (av === null && bv === null) return 0;
  if (av === null) return 1;
  if (bv === null) return -1;

  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    result = av - bv;
  } else {
    result = String(av).localeCompare(String(bv), 'en-AU', {
      numeric: true,
      sensitivity: 'base',
    });
  }
  return dir === 'asc' ? result : -result;
}

const CompetitionDayDashboard: React.FC<CompetitionDayDashboardProps> = ({ eventId }) => {
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<any[]>([]);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unreadableCount, setUnreadableCount] = useState(0);
  const [paymentFilter, setPaymentFilter] = useState<PaymentStatusFilter>('ALL');
  const [registrationFilter, setRegistrationFilter] = useState<RegistrationStatusFilter>('ALL');
  // #81: free-text search, combined (AND) with the status filters above.
  const [searchTerm, setSearchTerm] = useState('');
  const [memberRenewalMap, setMemberRenewalMap] = useState<Record<string, MemberRenewalInfo>>({});
  const [dismissedOverrides, setDismissedOverrides] = useState<Set<string>>(new Set());
  const [overrideLoading, setOverrideLoading] = useState<Set<string>>(new Set());
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Surfaced next to Refresh so a stale list is visibly stale. On 20 Sep 2026 a
  // snapshot from page-load time was mistaken for missing records.
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Defaults to most-recent check-in first, matching the previous fixed order.
  const [sortKey, setSortKey] = useState<RegSortKey>('checkInTime');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // #78: admin "Remove check-in".
  const { user, userId } = useAuth();
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // #163: approve (FINALIZED) from the list. The server refuses unless
  // payment is RECEIVED; the button mirrors that rule.
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rowMessage, setRowMessage] = useState<Record<string, { ok: boolean; text: string }>>({});

  const setRowMsg = (id: string, ok: boolean, text: string) => setRowMessage((m) => ({ ...m, [id]: { ok, text } }));

  /** Calls the server rule; returns an error message, or null on success. */
  const finaliseOnServer = async (regId: string): Promise<string | null> => {
    try {
      const res = await (client.mutations as any).finalizeCompetitionDayRegistration({ registrationId: regId });
      if (res?.errors?.length) throw new Error(res.errors.map((e: any) => e.message).join('; '));
      const body = parseLambdaResponse<{ success: boolean; message?: string; registration?: any }>(res?.data);
      if (!body?.success) return body?.message || 'Could not approve. Please try again.';
      setRegistrations((prev) => prev.map((r) => (r.id === regId ? { ...r, ...body.registration } : r)));
      return null;
    } catch (err: any) {
      console.error('Approve check-in failed:', err);
      return 'Could not approve. Please try again.';
    }
  };

  const handleApprove = async (reg: any) => {
    if (reg.paymentStatus !== 'RECEIVED' || reg.registrationStatus === 'FINALIZED') return;
    setApprovingId(reg.id);
    setRowMessage((m) => { const { [reg.id]: _drop, ...rest } = m; return rest; });
    try {
      const error = await finaliseOnServer(reg.id);
      if (error) setRowMsg(reg.id, false, error);
      else setRowMsg(reg.id, true, 'Approved.');
    } finally {
      setApprovingId(null);
    }
  };

  // #163 follow-up (user, 17 Oct 2026): two steps. An admin ticks "Mark
  // paid" when the person pays at the desk (cash or EFTPOS, the fee shown;
  // a different amount is changed in View Details), then approves. Each
  // person separately; no confirm. Recorded exactly as Registration Details'
  // "Mark payment received" (recordManualPayment: same fields and
  // PAYMENT_RECEIVED audit). Square payments arrive already RECEIVED.
  const handleMarkPaid = async (reg: any) => {
    setApprovingId(reg.id);
    setRowMessage((m) => { const { [reg.id]: _drop, ...rest } = m; return rest; });
    try {
      const paid = await recordManualPayment(client, reg, { email: user?.email || '', userId: userId || '' });
      setRegistrations((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...(paid || {}), paymentStatus: 'RECEIVED' } : r)));
      setRowMsg(reg.id, true, 'Marked paid. Approve to complete the check-in.');
    } catch (err) {
      console.error('Mark paid failed:', err);
      setRowMsg(reg.id, false, 'Could not mark as paid. Nothing was changed.');
    } finally {
      setApprovingId(null);
    }
  };

  // #163 follow-up: untick Paid (a desk payment ticked by mistake), only
  // before the check-in is approved and never for a Square payment.
  const handleUnmarkPaid = async (reg: any) => {
    if (reg.squarePaymentId || reg.registrationStatus === 'FINALIZED' || reg.paymentStatus !== 'RECEIVED') return;
    setApprovingId(reg.id);
    setRowMessage((m) => { const { [reg.id]: _drop, ...rest } = m; return rest; });
    try {
      const unpaid = await reverseManualPayment(client, reg, { email: user?.email || '', userId: userId || '' });
      setRegistrations((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...(unpaid || {}), paymentStatus: 'PENDING', paymentTimestamp: null } : r)));
      setRowMsg(reg.id, true, 'Payment unticked.');
    } catch (err) {
      console.error('Untick paid failed:', err);
      setRowMsg(reg.id, false, 'Could not untick the payment. Nothing was changed.');
    } finally {
      setApprovingId(null);
    }
  };

  // #163 follow-up (user, 17 Oct 2026): untick Checked in, with a confirm and
  // an optional reason; refused once the results are adjudicated or published.
  const [undoTarget, setUndoTarget] = useState<any | null>(null);
  const resultsDecided = !!(event?.adjudicatedAt || event?.publishedAt);
  const handleUndoCheckIn = async (reason: string) => {
    const reg = undoTarget;
    if (!reg) return;
    setApprovingId(reg.id);
    setRowMessage((m) => { const { [reg.id]: _drop, ...rest } = m; return rest; });
    try {
      const res = await (client.mutations as any).finalizeCompetitionDayRegistration({ registrationId: reg.id, undo: true, reason: reason || undefined });
      if (res?.errors?.length) throw new Error(res.errors.map((e: any) => e.message).join('; '));
      const body = parseLambdaResponse<{ success: boolean; message?: string; registration?: any }>(res?.data);
      if (!body?.success) {
        setRowMsg(reg.id, false, body?.message || 'Could not undo the check-in. Please try again.');
      } else {
        setRegistrations((prev) => prev.map((r) => (r.id === reg.id ? { ...r, ...body.registration } : r)));
        setRowMsg(reg.id, true, 'Check-in undone.');
      }
    } catch (err) {
      console.error('Undo check-in failed:', err);
      setRowMsg(reg.id, false, 'Could not undo the check-in. Please try again.');
    } finally {
      setApprovingId(null);
      setUndoTarget(null);
    }
  };

  /** The other unpaid, pending check-ins of this row's household at this competition (including this row). */
  const unpaidHousehold = (reg: any): any[] => {
    const fam = reg.memberId ? memberRenewalMap[reg.memberId]?.familyGroupId : null;
    if (!fam) return [];
    return registrations.filter((r) =>
      r.registrationStatus === 'PENDING' && r.paymentStatus !== 'RECEIVED' && r.memberId && memberRenewalMap[r.memberId]?.familyGroupId === fam);
  };

  // #163 follow-up (user, 17 Oct 2026): a family pays once and the desk marks
  // every registered member of that household paid in one tap. Only the people
  // who registered for this competition are listed, so "the family" here is
  // exactly those rows. Each row is still recorded and audited on its own
  // (recordManualPayment), with the household batch in the audit metadata.
  const handleMarkFamilyPaid = async (reg: any) => {
    const household = unpaidHousehold(reg);
    if (household.length < 2) return handleMarkPaid(reg);
    const total = household.reduce((t, r) => t + Number(r.feeAmount || 0), 0);
    setApprovingId(reg.id);
    let ok = 0;
    for (const r of household) {
      try {
        const paid = await recordManualPayment(client, r, { email: user?.email || '', userId: userId || '' }, {
          household: { familyGroupId: memberRenewalMap[r.memberId]?.familyGroupId, people: household.length, totalAmount: total },
        });
        setRegistrations((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...(paid || {}), paymentStatus: 'RECEIVED' } : x)));
        ok++;
        setRowMsg(r.id, true, 'Marked paid with the family. Approve to complete the check-in.');
      } catch (err) {
        console.error('Mark family paid failed for', r.id, err);
        setRowMsg(r.id, false, 'Could not mark as paid. Nothing was changed for this person.');
      }
    }
    if (ok < household.length) {
      setRowMsg(reg.id, false, `Marked ${ok} of ${household.length} family members paid. Check the rows marked with a warning.`);
    }
    setApprovingId(null);
  };

  const navigateToDetail = (registrationId: string) => {
    window.history.pushState({}, '', `/admin/competition-day/registration/${registrationId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  useEffect(() => {
    loadData();
  }, [eventId]);

  /*
    Auto-refresh every 30s while the dashboard is open.

    Reported live on competition morning, 20 Sep 2026: participants who had
    genuinely checked in and paid were absent from the list, and it looked like
    records were being dropped. They were not. loadData() ran once on mount and
    then only on an explicit Refresh click or an admin check-in through
    AdminCheckInPanel. The tab had been open while twelve more people checked
    in, so the screen showed a snapshot of the world as it was when it loaded —
    36 registrations against 48 in the table. Every tile reconciled perfectly
    against that stale snapshot, which is exactly why it read as data loss
    rather than staleness.

    On comp morning this screen is watched continuously while check-ins arrive,
    so it has to be live. Polling rather than a GraphQL subscription: this is a
    ~50-registration table for a few hours a month, the query is already
    paginated, and polling cannot miss an event or need reconnection handling.

    Refreshes are SILENT — they never set `loading`, so the table is never
    blanked and a sort or scroll position is never thrown away mid-conversation
    with a member at the desk. `lastUpdated` is rendered so staleness is
    visible rather than assumed.
  */
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadData({ silent: true });
    }, 30_000);
    return () => window.clearInterval(id);
  }, [eventId]);

  // Refresh immediately on returning to the tab, rather than waiting up to 30s.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadData({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [eventId]);

  useEffect(() => {
    applyFilters();
  }, [registrations, paymentFilter, registrationFilter, searchTerm]);

  const loadData = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
      setBannerDismissed(false);
    }
    setError('');

    try {
      // Load event details
      const eventData = await client.models.Event.get({ id: eventId });
      setEvent(eventData.data);

      /*
        Load ALL registrations for this event, following nextToken.

        This was a single unpaginated list() call with a filter, which is the
        DynamoDB filter-plus-limit trap: AppSync/Amplify runs a Scan with a
        FilterExpression, the scan reads one page, and the filter is applied
        AFTER that page is read. So the call returns only the matches that
        happened to fall inside the first scanned page — not all matching rows.

        Reported live on competition morning, 20 Sep 2026, as "UI refresh seems
        to be lagging": a member who had checked in at 06:47 did not appear.
        128 rows in the table, 39 of them for that event, so a single page could
        not be relied on to contain them all — and which ones came back varied
        between refreshes, which is why it looked like lag rather than a fault.

        MemberCatchSubmission already paginates this exact query; this matches it.
      */
      let registrationList: any[] = [];
      let unreadable = 0;
      let nextToken: string | null = null;
      do {
        const page: any = await (client.models.CompetitionDayRegistration as any).list({
          filter: { eventId: { eq: eventId } },
          limit: 1000,
          ...(nextToken ? { nextToken } : {}),
        });
        // A registration missing a required field comes back as null with a
        // GraphQL error. One such row used to crash the whole dashboard into
        // the error boundary (found on dev, 5 Oct 2026). Keep the readable
        // rows, count the rest, and say so above the table.
        const items: any[] = page.data || [];
        const readable = items.filter((r) => r && typeof r === 'object');
        unreadable += items.length - readable.length;
        if (page.errors?.length) {
          console.warn('CompetitionDayDashboard: unreadable registration records', page.errors.map((e: any) => e?.path?.join('.')));
        }
        registrationList = registrationList.concat(readable);
        nextToken = page.nextToken ?? null;
      } while (nextToken);

      setUnreadableCount(unreadable);
      setRegistrations(registrationList);
      setLastUpdated(new Date());

      // Batch-fetch member renewal statuses for registrations with a memberId
      const memberIds = registrationList
        .filter((reg: any) => reg.memberId)
        .map((reg: any) => reg.memberId as string);

      const uniqueMemberIds: string[] = Array.from(new Set(memberIds));
      const renewalMap: Record<string, MemberRenewalInfo> = {};

      await Promise.all(
        uniqueMemberIds.map(async (memberId: string) => {
          try {
            const { data: member } = await client.models.Member.get({ id: memberId });
            if (member) {
              renewalMap[memberId] = {
                renewalStatus: (member as any).renewalStatus || null,
                familyGroupId: (member as any).familyGroupId || null,
              };
            }
          } catch (err) {
            console.error(`Failed to fetch member ${memberId}:`, err);
          }
        })
      );

      setMemberRenewalMap(renewalMap);

      // Pre-populate dismissed overrides for registrations that already have override notes
      const alreadyOverridden = new Set<string>();
      registrationList.forEach((reg: any) => {
        if (reg.notes && reg.notes.includes('Membership expiry override')) {
          alreadyOverridden.add(reg.id);
        }
      });
      setDismissedOverrides(alreadyOverridden);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load competition data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...registrations];

    if (paymentFilter !== 'ALL') {
      filtered = filtered.filter(reg => reg.paymentStatus === paymentFilter);
    }

    if (registrationFilter !== 'ALL') {
      filtered = filtered.filter(reg => reg.registrationStatus === registrationFilter);
    }

    // #81: case-insensitive, trimmed match on name, member number or email,
    // combined (AND) with the status filters above, before sorting.
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter((reg) => {
        const name = (reg.participantName || '').toLowerCase();
        const memberNumber = (reg.memberNumber || '').toLowerCase();
        const email = (reg.participantEmail || '').toLowerCase();
        return name.includes(term) || memberNumber.includes(term) || email.includes(term);
      });
    }

    // Deterministic base order (most recent check-in first). The user-selected
    // column sort is applied on top of this at render — see sortedRegistrations.
    filtered.sort((a, b) => {
      const timeA = new Date(a.checkInTimestamp || 0).getTime();
      const timeB = new Date(b.checkInTimestamp || 0).getTime();
      return timeB - timeA;
    });

    setFilteredRegistrations(filtered);
  };

  const calculateStatistics = () => {
    const finalizedRegistrations = registrations.filter(
      reg => reg.registrationStatus === 'FINALIZED'
    );

    const totalParticipants = finalizedRegistrations.length;
    const totalRevenue = finalizedRegistrations.reduce(
      (sum, reg) => sum + (reg.feeAmount || 0),
      0
    );

    const pendingPayments = registrations.filter(
      reg => reg.paymentStatus === 'PENDING'
    ).length;

    const pendingFinalizations = registrations.filter(
      reg => reg.registrationStatus === 'PENDING'
    ).length;

    return {
      totalParticipants,
      totalRevenue,
      pendingPayments,
      pendingFinalizations,
      totalRegistrations: registrations.length,
    };
  };

  const formatTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return formatClubTime(timestamp, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return formatClubDate(timestamp, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleViewDetails = (registrationId: string) => {
    navigateToDetail(registrationId);
  };

  const getRenewalStatusForRegistration = (reg: any): string | null => {
    if (!reg.memberId) return null;
    const info = memberRenewalMap[reg.memberId];
    return info?.renewalStatus || null;
  };

  const isExpiredOrLapsed = (status: string | null): boolean => {
    return status === 'EXPIRED' || status === 'LAPSED';
  };

  const handleSort = (key: RegSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Time and money read most usefully largest-first; text reads A-Z.
      setSortDir(key === 'checkInTime' || key === 'fee' ? 'desc' : 'asc');
    }
  };

  // Sorting is derived at render rather than folded into applyFilters, so
  // changing sort never needs to re-run the filter effect or refetch.
  const sortedRegistrations = React.useMemo(
    () =>
      [...filteredRegistrations].sort((a, b) =>
        compareRegs(a, b, sortKey, sortDir, getRenewalStatusForRegistration),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRegistrations, sortKey, sortDir, memberRenewalMap],
  );

  // #163 redesign: households (members sharing a familyGroupId) are grouped
  // under one header row with a single "Paid" tick, at the position of their
  // first member in the current sort; everyone else stays a normal row.
  type DisplayRow =
    | { kind: 'family'; key: string; surname: string; members: any[] }
    | { kind: 'row'; reg: any; inFamily: boolean };
  const displayRows = React.useMemo<DisplayRow[]>(() => {
    const famOf = (r: any) => (r.memberId ? memberRenewalMap[r.memberId]?.familyGroupId || null : null);
    const counts = new Map<string, number>();
    for (const r of sortedRegistrations) {
      const f = famOf(r);
      if (f) counts.set(f, (counts.get(f) || 0) + 1);
    }
    const out: DisplayRow[] = [];
    const done = new Set<string>();
    for (const r of sortedRegistrations) {
      const f = famOf(r);
      if (f && (counts.get(f) || 0) >= 2) {
        if (done.has(f)) continue;
        done.add(f);
        const members = sortedRegistrations.filter((x) => famOf(x) === f);
        const last = String(members[0]?.participantName || '').trim().split(/\s+/).pop() || 'Family';
        out.push({ kind: 'family', key: f, surname: last, members });
        for (const m of members) out.push({ kind: 'row', reg: m, inFamily: true });
      } else {
        out.push({ kind: 'row', reg: r, inFamily: false });
      }
    }
    return out;
  }, [sortedRegistrations, memberRenewalMap]);

  const renderMembershipBadge = (renewalStatus: string | null, hasMemberId: boolean) => {
    if (!hasMemberId) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
          N/A
        </span>
      );
    }

    switch (renewalStatus) {
      case 'CURRENT':
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
            Current
          </span>
        );
      case 'PENDING_RENEWAL':
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800">
            Pending Renewal
          </span>
        );
      case 'EXPIRED':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
            ⚠️ Expired
          </span>
        );
      case 'LAPSED':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-800">
            ⚠️ Lapsed
          </span>
        );
      default:
        return (
          <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
            Unknown
          </span>
        );
    }
  };

  /**
   * #78: remove a check-in. Admin-only: the model grants delete to
   * AdminsVerified, so this uses the admin's userPool client directly (the
   * member-facing cancelCompetitionDayRegistration Lambda only lets members
   * cancel their own unpaid rows). Paid rows can be removed too; the dialog
   * says the refund is separate. Audit-logged.
   */
  const handleRemoveCheckIn = async (reason?: string) => {
    const reg = removeTarget;
    if (!reg) return;
    const wasPaid = isRegistrationPaid(reg);
    setRemoving(true);
    setRemoveError(null);
    try {
      const { errors } = await (client.models.CompetitionDayRegistration as any).delete({ id: reg.id });
      if (errors?.length) throw new Error(errors.map((e: any) => e.message).join('; '));
      await logAuditEntry(user?.email || '', userId || '', {
        actionType: 'COMPETITION_CHECKIN_REMOVED',
        targetEntityType: 'CompetitionDayRegistration',
        targetEntityId: reg.id,
        description: `Removed check-in for ${reg.participantName || 'unknown'} (${wasPaid ? (reg.squarePaymentId ? `paid online, refund $${reg.feeAmount} in Square` : `paid at the desk, give back $${reg.feeAmount}`) : 'unpaid'})${reason ? `. Reason: ${reason}` : ''}`,
        metadata: {
          // #163: what is owed back, how, and why (a reason is required once approved).
          refundOwed: wasPaid ? reg.feeAmount : 0,
          refundMethod: wasPaid ? (reg.squarePaymentId ? 'SQUARE' : 'DESK') : null,
          registrationStatus: reg.registrationStatus,
          reason: reason || null,
          eventId: reg.eventId,
          participantName: reg.participantName,
          participantEmail: reg.participantEmail,
          registrationType: reg.registrationType,
          memberId: reg.memberId,
          paymentStatus: reg.paymentStatus,
          squarePaymentId: reg.squarePaymentId,
          feeAmount: reg.feeAmount,
        },
      });
      setRegistrations((prev) => prev.filter((r) => r.id !== reg.id));
      setRemoveTarget(null);
    } catch (err: any) {
      console.error('Failed to remove check-in:', err);
      setRemoveError(err?.message ? `Could not remove the check-in: ${err.message}` : 'Could not remove the check-in. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  const handleOverride = async (reg: any) => {
    const renewalStatus = getRenewalStatusForRegistration(reg);
    if (!renewalStatus) return;

    setOverrideLoading((prev) => new Set(prev).add(reg.id));

    try {
      const today = formatClubDate(new Date(), {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      const overrideNote = `Membership expiry override: ${renewalStatus} member allowed to participate on ${today}`;
      const existingNotes = reg.notes || '';
      const updatedNotes = existingNotes ? `${existingNotes}\n${overrideNote}` : overrideNote;

      await client.models.CompetitionDayRegistration.update({
        id: reg.id,
        notes: updatedNotes,
      });

      // Update local registration data
      setRegistrations((prev) =>
        prev.map((r) => (r.id === reg.id ? { ...r, notes: updatedNotes } : r))
      );

      setDismissedOverrides((prev) => new Set(prev).add(reg.id));
    } catch (err) {
      console.error('Failed to record override:', err);
      alert('Failed to record override. Please try again.');
    } finally {
      setOverrideLoading((prev) => {
        const next = new Set(prev);
        next.delete(reg.id);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading competition data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  const stats = calculateStatistics();

  // Count registrations with expired/lapsed members that haven't been overridden yet
  const expiredLapsedRegistrations = registrations.filter((reg: any) => {
    const status = getRenewalStatusForRegistration(reg);
    return isExpiredOrLapsed(status) && !dismissedOverrides.has(reg.id);
  });
  const expiredLapsedCount = expiredLapsedRegistrations.length;

  const handleBannerAcknowledge = async () => {
    // Record override notes for all un-overridden expired/lapsed registrations
    const regsToOverride = expiredLapsedRegistrations;
    for (const reg of regsToOverride) {
      await handleOverride(reg);
    }
    setBannerDismissed(true);
  };

  return (
    <div className="space-y-6 p-2 sm:p-0">
      {/* Header */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <button
            onClick={() => {
              window.history.pushState({}, '', '/admin');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Events
          </button>
          {/* #134 D5: always shown for a competition, not gated on whether
              stored competition state happens to be set (previously hidden for any
              event never transitioned, since the attribute was simply
              absent). */}
          {event?.eventType === 'COMPETITION' && (
            <button
              onClick={() => {
                window.history.pushState({}, '', `/admin/adjudication/${eventId}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-fishing-blue border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fishing-blue"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Adjudication
            </button>
          )}
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Competition Day Dashboard
        </h1>
        {event && (
          <div className="text-gray-600">
            <p className="font-medium">{event.title}</p>
            <p className="text-sm">{formatDate(event.eventDate)}</p>
          </div>
        )}
      </div>

      {/* Statistics */}
      {/* Summary tiles — clickable, each applies the filter combination it
          represents so an admin can go straight from a number to the rows
          behind it. The active tile is outlined so it's clear which filter
          is driving the list below. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        <button
          type="button"
          onClick={() => { setPaymentFilter('ALL'); setRegistrationFilter('ALL'); }}
          aria-pressed={paymentFilter === 'ALL' && registrationFilter === 'ALL'}
          className={`bg-white p-3 sm:p-4 rounded-lg shadow-md text-left transition-all hover:shadow-lg hover:ring-2 hover:ring-gray-300 ${
            paymentFilter === 'ALL' && registrationFilter === 'ALL' ? 'ring-2 ring-gray-500' : ''
          }`}
        >
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Registrations</div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalRegistrations}</div>
        </button>
        <button
          type="button"
          onClick={() => { setPaymentFilter('ALL'); setRegistrationFilter('FINALIZED'); }}
          aria-pressed={registrationFilter === 'FINALIZED' && paymentFilter === 'ALL'}
          className={`bg-white p-3 sm:p-4 rounded-lg shadow-md text-left transition-all hover:shadow-lg hover:ring-2 hover:ring-green-300 ${
            registrationFilter === 'FINALIZED' && paymentFilter === 'ALL' ? 'ring-2 ring-green-500' : ''
          }`}
        >
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Finalised Participants</div>
          <div className="text-xl sm:text-2xl font-bold text-green-600">{stats.totalParticipants}</div>
        </button>
        {/* Revenue is a dollar sum of finalised registrations, not a status —
            clicking it shows the same finalised rows the total is derived
            from, so the number can be reconciled against the list. */}
        <button
          type="button"
          onClick={() => { setPaymentFilter('ALL'); setRegistrationFilter('FINALIZED'); }}
          aria-pressed={registrationFilter === 'FINALIZED' && paymentFilter === 'ALL'}
          className={`bg-white p-3 sm:p-4 rounded-lg shadow-md text-left transition-all hover:shadow-lg hover:ring-2 hover:ring-blue-300 ${
            registrationFilter === 'FINALIZED' && paymentFilter === 'ALL' ? 'ring-2 ring-blue-500' : ''
          }`}
        >
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Total Revenue</div>
          <div className="text-xl sm:text-2xl font-bold text-blue-600">${stats.totalRevenue}</div>
        </button>
        <button
          type="button"
          onClick={() => { setPaymentFilter('PENDING'); setRegistrationFilter('ALL'); }}
          aria-pressed={paymentFilter === 'PENDING' && registrationFilter === 'ALL'}
          className={`bg-white p-3 sm:p-4 rounded-lg shadow-md text-left transition-all hover:shadow-lg hover:ring-2 hover:ring-yellow-300 ${
            paymentFilter === 'PENDING' && registrationFilter === 'ALL' ? 'ring-2 ring-yellow-500' : ''
          }`}
        >
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Pending Payments</div>
          <div className="text-xl sm:text-2xl font-bold text-yellow-600">{stats.pendingPayments}</div>
        </button>
        <button
          type="button"
          onClick={() => { setPaymentFilter('ALL'); setRegistrationFilter('PENDING'); }}
          aria-pressed={registrationFilter === 'PENDING' && paymentFilter === 'ALL'}
          className={`bg-white p-3 sm:p-4 rounded-lg shadow-md text-left transition-all hover:shadow-lg hover:ring-2 hover:ring-orange-300 ${
            registrationFilter === 'PENDING' && paymentFilter === 'ALL' ? 'ring-2 ring-orange-500' : ''
          }`}
        >
          <div className="text-xs sm:text-sm text-gray-600 mb-1">Pending Finalizations</div>
          <div className="text-xl sm:text-2xl font-bold text-orange-600">{stats.pendingFinalizations}</div>
        </button>
      </div>

      {/* Admin check-in on behalf */}
      {unreadableCount > 0 && (
        <div role="alert" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unreadableCount === 1 ? '1 check-in record' : `${unreadableCount} check-in records`} couldn&apos;t be read and{' '}
          {unreadableCount === 1 ? 'is' : 'are'} not shown below. The counts above leave {unreadableCount === 1 ? 'it' : 'them'} out.
          Please tell the website admin.
        </div>
      )}
      <AdminCheckInPanel eventId={eventId} onCheckedIn={loadData} />

      {/*
        Active-filter banner. 20 Sep 2026, during the September competition:
        two checked-in, paid participants appeared to be missing from the list.
        They were not — both records existed and the fetch paginates correctly.
        The list was filtered to FINALIZED, which hid every registration not yet
        finalised by an admin.

        The "Total Revenue" tile set that filter while being the only tile
        without aria-pressed or an active ring, so nothing indicated a filter
        was applied and a *different* tile lit up instead (its aria-pressed
        condition matches the same ALL+FINALIZED state). The tile is fixed
        above, but this banner is the part that fails safe: any filter, set by
        any control now or in future, is stated in words above the table with a
        one-click way out. A count of "showing X of Y" is the difference between
        "we've lost data" and "this view is filtered".
      */}
      {(paymentFilter !== 'ALL' || registrationFilter !== 'ALL') && (
        <div
          role="status"
          className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2"
        >
          <p className="text-sm text-amber-900">
            <span className="font-semibold">Filtered view</span> — showing{' '}
            <span className="font-semibold">{filteredRegistrations.length}</span> of{' '}
            <span className="font-semibold">{registrations.length}</span> registrations
            {paymentFilter !== 'ALL' && <> · payment: <span className="font-semibold">{paymentFilter === 'RECEIVED' ? 'Received' : 'Pending'}</span></>}
            {registrationFilter !== 'ALL' && <> · status: <span className="font-semibold">{registrationFilter === 'FINALIZED' ? 'Finalised' : 'Pending'}</span></>}
            . Participants outside this filter are hidden, not missing.
          </p>
          <button
            type="button"
            onClick={() => { setPaymentFilter('ALL'); setRegistrationFilter('ALL'); }}
            className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md whitespace-nowrap"
          >
            Show all {registrations.length}
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        {/* #81: free-text search over name, member number and email. Combined
            (AND) with the status filters below, applied before sorting. Its
            own row so the box can run full width on a phone. */}
        <div className="mb-4">
          <label htmlFor="registration-search" className="block text-sm font-medium text-gray-700 mb-1">
            Search registrations
          </label>
          <div className="flex items-center gap-2">
            <input
              id="registration-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name, member number or email"
              className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[44px]"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
                className="shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {/* Count of the currently filtered/searched rows, announced to
              screen readers so a search result isn't a silent table change. */}
          <p className="mt-1 text-xs text-gray-500" aria-live="polite">
            Showing {sortedRegistrations.length} of {registrations.length}
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="payment-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <select
              id="payment-status-filter"
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value as PaymentStatusFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="RECEIVED">Received</option>
            </select>
          </div>
          <div>
            <label htmlFor="registration-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Registration Status
            </label>
            <select
              id="registration-status-filter"
              value={registrationFilter}
              onChange={(e) => setRegistrationFilter(e.target.value as RegistrationStatusFilter)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All</option>
              <option value="PENDING">Pending</option>
              <option value="FINALIZED">Finalised</option>
            </select>
          </div>
          <div className="flex items-end gap-3">
            <button
              onClick={() => loadData()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh
            </button>
            {/* Staleness must be visible, not inferred — see the auto-refresh
                note above loadData for why this exists. */}
            <p className="text-xs text-gray-500 pb-2" aria-live="polite">
              {lastUpdated
                ? <>Updated {formatTime(lastUpdated.toISOString())} · auto-refreshes every 30s</>
                : <>Loading…</>}
            </p>
          </div>
        </div>
      </div>

      {/* Expired/Lapsed Members Warning Banner */}
      {expiredLapsedCount > 0 && !bannerDismissed && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-red-600 text-xl">⚠️</span>
            <div>
              <p className="text-sm font-medium text-red-800">
                {expiredLapsedCount} registration{expiredLapsedCount !== 1 ? 's' : ''} with expired or lapsed membership
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                These members do not have current memberships. Review the highlighted rows below.
              </p>
            </div>
          </div>
          <button
            onClick={handleBannerAcknowledge}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 whitespace-nowrap"
          >
            Acknowledge &amp; Proceed
          </button>
        </div>
      )}

      {/* Registrations Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh label="Participant" columnKey="participant" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Type" columnKey="type" activeKey={sortKey} dir={sortDir} onSort={handleSort} className="hidden lg:table-cell" />
                <SortableTh label="Membership" columnKey="membership" activeKey={sortKey} dir={sortDir} onSort={handleSort} sortHint="membership status" />
                <SortableTh label="Check-In" columnKey="checkInTime" activeKey={sortKey} dir={sortDir} onSort={handleSort} sortHint="check-in time" className="hidden md:table-cell" />
                <SortableTh label="Fee" columnKey="fee" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortableTh label="Paid" columnKey="payment" activeKey={sortKey} dir={sortDir} onSort={handleSort} sortHint="payment (unpaid first)" />
                <SortableTh label="Checked in" columnKey="status" activeKey={sortKey} dir={sortDir} onSort={handleSort} sortHint="checked in (not yet first)" />
                <th scope="col" className="px-3 py-3"><span className="sr-only">More actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRegistrations.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-center text-gray-500">
                    No registrations found
                  </td>
                </tr>
              ) : (
                displayRows.map((item) => {
                  if (item.kind === 'family') {
                    const unpaid = item.members.filter((m) => m.registrationStatus === 'PENDING' && m.paymentStatus !== 'RECEIVED');
                    const allPaid = item.members.every((m) => m.paymentStatus === 'RECEIVED');
                    const unpaidTotal = unpaid.reduce((t, m) => t + Number(m.feeAmount || 0), 0);
                    const busy = approvingId !== null;
                    const famLabel = `${item.surname} family`;
                    return (
                      <tr key={`fam-${item.key}`} className="bg-blue-50/60">
                        <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-900">
                          {famLabel} <span className="font-normal text-gray-600">· {item.members.length} registered{unpaid.length ? ` · ${unpaid.length} unpaid` : ''}</span>
                        </td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900 whitespace-nowrap">
                          {unpaid.length ? `$${unpaidTotal.toFixed(2)} due` : ''}
                        </td>
                        <td className="px-4 py-2">
                          <label className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-5 w-5 rounded border-gray-400 text-green-600 focus:ring-fishing-blue disabled:cursor-not-allowed"
                              checked={allPaid}
                              disabled={allPaid || unpaid.length === 0 || busy}
                              onChange={() => { if (unpaid.length) handleMarkFamilyPaid(unpaid[0]); }}
                              aria-label={allPaid ? `Paid: ${famLabel}, everyone has paid` : `Paid: ${famLabel}, ${unpaid.length} unpaid, $${unpaidTotal.toFixed(2)}`}
                            />
                            <span className="text-xs text-gray-600">{allPaid ? 'All paid' : 'Whole family'}</span>
                          </label>
                        </td>
                        <td colSpan={2} />
                      </tr>
                    );
                  }
                  const reg = item.reg;
                  const renewalStatus = getRenewalStatusForRegistration(reg);
                  const showWarning = isExpiredOrLapsed(renewalStatus) && !dismissedOverrides.has(reg.id);
                  const isOverridden = dismissedOverrides.has(reg.id) && isExpiredOrLapsed(renewalStatus);
                  const name = reg.participantName || 'this person';
                  const paid = reg.paymentStatus === 'RECEIVED';
                  const final = reg.registrationStatus === 'FINALIZED';
                  const busy = approvingId !== null;
                  const hintId = `checkin-hint-${reg.id}`;

                  return (
                    <tr
                      key={reg.id}
                      className={
                        showWarning
                          ? 'bg-red-50 hover:bg-red-100'
                          : 'hover:bg-gray-50'
                      }
                    >
                      <td className={`px-4 py-3 ${item.inFamily ? 'pl-8' : ''}`}>
                        <div className="text-sm font-medium text-gray-900">{reg.participantName}</div>
                        <div className="text-xs text-gray-500">
                          {[reg.memberNumber, reg.participantEmail].filter(Boolean).join(' · ')}
                        </div>
                        <div aria-live="polite" className="text-xs">
                          {rowMessage[reg.id] && (
                            <span className={rowMessage[reg.id].ok ? 'text-green-700' : 'text-red-700'}>
                              {rowMessage[reg.id].ok ? '✓ ' : '⚠ '}{rowMessage[reg.id].text}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                          {reg.registrationType?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {renderMembershipBadge(renewalStatus, !!reg.memberId)}
                          {showWarning && (
                            <button
                              onClick={() => handleOverride(reg)}
                              disabled={overrideLoading.has(reg.id)}
                              className="text-xs px-2 py-1 rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 w-fit"
                            >
                              {overrideLoading.has(reg.id) ? 'Saving…' : 'Override'}
                            </button>
                          )}
                          {isOverridden && (
                            <span className="text-xs text-gray-500 italic">Overridden</span>
                          )}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(reg.checkInTimestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${reg.feeAmount}
                        {reg.feeAdjustmentReason && (
                          <span
                            title={reg.feeAdjustmentReason}
                            className="ml-1 inline-flex items-center cursor-help"
                          >
                            <svg className="h-4 w-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <label className="inline-flex min-h-[44px] min-w-[44px] items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-gray-400 text-green-600 focus:ring-fishing-blue disabled:cursor-not-allowed"
                            checked={paid}
                            // Untick allowed for a desk payment before check-in; Square and
                            // approved check-ins stay locked.
                            disabled={final || busy || (paid && !!reg.squarePaymentId)}
                            onChange={() => (paid ? handleUnmarkPaid(reg) : handleMarkPaid(reg))}
                            aria-label={`Paid: ${name}`}
                            title={paid && reg.squarePaymentId ? 'Paid online: refund in Square' : final ? 'Checked in: payment locked' : undefined}
                          />
                          {paid && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${reg.squarePaymentId ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>
                              {reg.squarePaymentId ? 'Online' : 'Cash'}
                            </span>
                          )}
                        </label>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <label className={`inline-flex min-h-[44px] min-w-[44px] items-center gap-2 ${(final ? !resultsDecided : paid) ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                          <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-gray-400 text-blue-600 focus:ring-fishing-blue disabled:cursor-not-allowed"
                            checked={final}
                            disabled={busy || (final ? resultsDecided : !paid)}
                            onChange={() => (final ? setUndoTarget(reg) : handleApprove(reg))}
                            aria-label={`Checked in: ${name}`}
                            aria-describedby={!paid && !final ? hintId : undefined}
                            title={!paid && !final ? 'Payment not received' : undefined}
                          />
                          {!paid && !final && <span id={hintId} className="text-xs text-gray-400">Not paid</span>}
                        </label>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right">
                        <RowMenu
                          label={`More actions for ${name}`}
                          items={[
                            { label: 'View details', onSelect: () => handleViewDetails(reg.id) },
                            { label: 'Remove check-in', danger: true, onSelect: () => { setRemoveError(null); setRemoveTarget(reg); } },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <RemoveCheckInDialog
        registration={removeTarget}
        paid={removeTarget ? isRegistrationPaid(removeTarget) : false}
        removing={removing}
        error={removeError}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={handleRemoveCheckIn}
      />
      <UndoCheckInDialog
        registration={undoTarget}
        busy={approvingId !== null}
        onCancel={() => setUndoTarget(null)}
        onConfirm={handleUndoCheckIn}
      />

    </div>
  );
};

export default CompetitionDayDashboard;
